import { Maybe, Option } from "@jutro/types";
import { OptionWithSideElements } from "@jutro/ui/dist/Select/tools";
import dayjs, { Dayjs } from "dayjs";
import { range } from "ramda";
import { z } from "zod";
import {
  EdmGenreType,
  EdmLeaveType,
  EdmMedicalAppShiftWithoutVisitFragment,
  EdmMedicalAppShiftWorkPlace,
  EdmNonWorkingPeriod,
  EdmWorkedHours,
} from "lib/graphql/megaSchema";
import { organizationClinicOption } from "lib/hooks/useVojevodshipOptions";
import { isMidwife, isNurse } from "lib/tools/auth";
import { StaffGenreFilter, visitTypeLabelMap } from "views/StaffSchedule/tools";

export type SlotActionModalMode = "create" | "edit" | "delete" | "cancelDelete";

export type StaffOption = {
  id: string;
  genre: EdmGenreType;
  npwz: Maybe<string>;
  acceptChildren: boolean;
  avatarSrc?: string | null;
  workedHours?: EdmWorkedHours | null;
};

type OnSiteOptions = Option<EdmMedicalAppShiftWorkPlace>;
type LeaveTypeOptions = Option<EdmLeaveType>;

const genreEnum = z.enum([
  "ASSISTANT",
  "DOCTOR",
  "NURSE",
  "RADIOLOGIST",
  "STAFF",
  "CLINIC_MANAGER",
  "MIDWIFE",
]);

const visitTypeEnum = z.enum([
  "COLLECTION",
  "DOCTOR_HOUSE_CALL",
  "DOCTOR_TELEMEDICAL_INFECTION",
  "EXPRESS_APPOINTMENT",
  "IN_PERSON",
  "NURSE_HOUSE_CALL",
  "NURSE_IN_PERSON_INFECTION",
  "PRESCRIPTION_APPOINTMENT",
  "PROCEDURE",
  "RADIOLOGY_APPOINTMENT",
  "SICK_CHILDREN",
  "SPECIALIST",
  "TELEMEDICAL",
  "MIDWIFE_HOUSE_CALL",
  "MIDWIFE_IN_PERSON",
  "DIETETICS",
  "HEALTHY_CHILDREN_CHECKUP",
  "HEALTHY_CHILDREN_CHECKUP_AND_VACCINE",
  "HEALTHY_CHILDREN_PATRONAGE",
  "HEALTHY_CHILDREN_VACCINE",
  "NURSE_IN_PERSON",
  "NURSE_HEALTHY_CHILDREN",
]);

const leaveReasonEnum = z.enum([
  "PAID_HOLIDAY",
  "UNPAID_HOLIDAY",
  "OVERTIME",
  "BREAK",
  "ILLNESS",
  "OFFICE_VENTILATION",
  "MEETING",
]);

const workPlaceEnum = z.enum(["IN_CLINIC", "IN_HOME"]);

const changeRepeatingEnum = z.enum(["DO_NOT_REPEAT", "WEEKDAYS", "CUSTOM"]);

const daysEnum = z.enum([
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
]);

export const shiftFormSchema = z
  .object({
    organizationId: z.object(
      {
        label: z.string(),
        value: z.string(),
      },
      { required_error: "To pole jest wymagane" },
    ),
    staff: z.object(
      {
        label: z.string(),
        value: z.object({
          id: z.string(),
          genre: genreEnum,
          npwz: z.string().nullable().optional(),
          acceptChildren: z.boolean(),
        }),
      },
      {
        required_error: "To pole jest wymagane",
        invalid_type_error: "To pole jest wymagane",
      },
    ),
    visitTypes: z
      .array(
        z.object({
          label: z.string(),
          value: visitTypeEnum,
        }),
      )
      .min(1, "Wybierz przynajmniej jeden typ wizyty"),
    isCustomVisitDuration: z.boolean(),
    overrideStaffVisitDuration: z
      .record(visitTypeEnum, z.number().optional().nullable())
      .optional(),
    workPlace: z.object(
      {
        label: z.string(),
        value: workPlaceEnum,
      },
      { required_error: "To pole jest wymagane" },
    ),
    acceptingNfz: z.boolean(),
    acceptingPriv: z.boolean(),
    changeRepeating: changeRepeatingEnum,
    dateStartFrom: z.date({ message: "To pole jest wymagane" }),
    dateEnded: z.date().optional().nullable(),
    availableDays: z.array(daysEnum),
    timeFrom: z
      .string({ required_error: "Te pola są wymagane" })
      .min(5, { message: "Te pola są wymagane" }),
    timeTo: z
      .string({ required_error: "Te pola są wymagane" })
      .min(5, { message: "Te pola są wymagane" }),

    comment: z.string().optional().nullable(),
  })
  .refine(
    (fields) => {
      const { acceptingNfz, acceptingPriv } = fields;
      return acceptingPriv || acceptingNfz;
    },
    {
      message: "Przynajmniej jedna opcja musi być zaznaczona",
      path: ["acceptingNfz"],
    },
  )
  .refine(
    (fields) => {
      const { changeRepeating, dateEnded } = fields;

      if (changeRepeating === "DO_NOT_REPEAT") {
        return true;
      }

      return Boolean(dateEnded);
    },
    {
      message: "To pole jest wymagane",
      path: ["dateEnded"],
    },
  )
  .refine(
    (fields) => {
      const { changeRepeating, dateStartFrom, dateEnded } = fields;

      if (changeRepeating === "DO_NOT_REPEAT") {
        return true;
      }

      return dayjs(dateEnded).isSameOrAfter(dateStartFrom);
    },
    {
      message: "Niepoprawna data zakończenia",
      path: ["dateEnded"],
    },
  )
  .refine(
    (fields) => {
      const { changeRepeating, availableDays } = fields;

      if (changeRepeating !== "CUSTOM") {
        return true;
      }

      return availableDays.length > 0;
    },
    {
      message: "To pole jest wymagane",
      path: ["availableDays"],
    },
  )
  .refine(
    (fields) => {
      const { timeFrom, timeTo } = fields;

      if (!/^\d{2}:\d{2}$/.test(timeFrom) || !/^\d{2}:\d{2}$/.test(timeTo)) {
        return false;
      }

      const [timeFromHour, timeFromMinute] = timeFrom.split(":").map(Number);
      const [timeToHour, timeToMinute] = timeTo.split(":").map(Number);

      const start = dayjs()
        .hour(timeFromHour)
        .minute(timeFromMinute)
        .second(0)
        .millisecond(0);

      const end = dayjs()
        .hour(timeToHour)
        .minute(timeToMinute)
        .second(0)
        .millisecond(0);

      return dayjs(end).isAfter(start);
    },
    {
      message:
        "Czas zakończenia zmiany nie może być wcześniejszy niż czas jej rozpoczęcia",
      path: ["timeTo"],
    },
  );

export const nonWorkingPeriodFormSchema = z
  .object({
    staff: z.object(
      {
        label: z.string(),
        value: z.object({
          id: z.string(),
          genre: genreEnum,
          npwz: z.string().nullable().optional(),
        }),
      },
      {
        required_error: "To pole jest wymagane",
        invalid_type_error: "To pole jest wymagane",
      },
    ),
    leaveReason: z.object(
      {
        label: z.string(),
        value: leaveReasonEnum,
      },
      { required_error: "To pole jest wymagane" },
    ),
    day: z.date({ message: "To pole jest wymagane" }),
    timeFrom: z
      .string({ required_error: "Te pola są wymagane" })
      .min(5, { message: "Te pola są wymagane" }),
    timeTo: z
      .string({ required_error: "Te pola są wymagane" })
      .min(5, { message: "Te pola są wymagane" }),
    comment: z.string().optional().nullable(),
  })
  .refine(
    (fields) => {
      const { timeFrom, timeTo } = fields;
      if (!/^\d{2}:\d{2}$/.test(timeFrom) || !/^\d{2}:\d{2}$/.test(timeTo)) {
        return false;
      }

      const start = formatTimeToDate(fields.timeFrom, fields.day).toISOString();
      const end = formatTimeToDate(fields.timeTo, fields.day).toISOString();

      return start < end;
    },
    {
      message:
        "Czas zakończenia zmiany nie może być wcześniejszy niż czas jej rozpoczęcia",
      path: ["timeTo"],
    },
  );

export const workingHoursFormSchema = z.object({
  hours: z
    .string({ required_error: "Te pola są wymagane" })
    .min(1, { message: "Te pola są wymagane" })
    .max(3, { message: "Te pola są wymagane" }),
  minutes: z
    .string()
    .min(1, { message: "Te pola są wymagane" })
    .max(2, { message: "Te pola są wymagane" }),
});

export type ShiftFormSchemaType = z.infer<typeof shiftFormSchema>;
export type NonWorkingPeriodSchemaType = z.infer<
  typeof nonWorkingPeriodFormSchema
>;
export type WorkingHoursSchemaType = z.infer<typeof workingHoursFormSchema>;

export type ChangeRepeating = z.infer<typeof changeRepeatingEnum>;

export type Days = z.infer<typeof daysEnum>;

export const workPlaceOptions: OnSiteOptions[] = [
  {
    label: "W przychodni",
    value: "IN_CLINIC",
  },
  {
    label: "Zdalnie",
    value: "IN_HOME",
  },
];

export const leaveTypeOptions: LeaveTypeOptions[] = [
  {
    label: "Przerwa",
    value: "BREAK",
  },
  {
    label: "Choroba",
    value: "ILLNESS",
  },
  { label: "Wietrzenie", value: "OFFICE_VENTILATION" },
  { label: "Nadgodziny", value: "OVERTIME" },
  { label: "Urlop płatny", value: "PAID_HOLIDAY" },
  { label: "Urlop bezpłatny", value: "UNPAID_HOLIDAY" },
  { label: "Zebranie", value: "MEETING" },
];

export const changeRepeatingOptions: Option<ChangeRepeating>[] = [
  { label: "Nie powtarzaj", value: "DO_NOT_REPEAT" },
  { label: "W dni powszednie (pon-pt)", value: "WEEKDAYS" },
  { label: "Niestandardowo", value: "CUSTOM" },
];

export const daysOptions: Option<Days>[] = [
  { value: "Monday", label: "poniedziałek" },
  { value: "Tuesday", label: "wtorek" },
  { value: "Wednesday", label: "środa" },
  { value: "Thursday", label: "czwartek" },
  { value: "Friday", label: "piątek" },
];

export const isDayInRange = (dayKey: Days, from: Date, to: Date) => {
  const fromDay = dayjs(from);
  const toDay = dayjs(to);
  const dayNumber =
    daysOptions.findIndex((option) => option.value === dayKey) + 1;

  for (let d = fromDay; d.isSameOrBefore(toDay, "day"); d = d.add(1, "day")) {
    if (d.day() === dayNumber) return true;
  }

  return false;
};

export const getColumnBackgroundColor = (day: dayjs.Dayjs) => {
  const weekendDays = [0, 6];

  if (day.isSame(dayjs(), "day")) {
    return "bg-jutro-new-blue-100";
  }

  if (weekendDays.includes(day.day())) {
    return "bg-jutro-new-warm-gray-200";
  }

  return "bg-transparent";
};

export const generateDaysInCurrentMonthRow = (from: Dayjs, to: Dayjs) => {
  const distance = to.diff(from, "day");
  return range(0, distance + 1).map((val) => from.add(val, "day"));
};

export const getShiftFormDefaultValues = (
  staffOptions: OptionWithSideElements<StaffOption>[] | undefined,
  selectedBlockStaffId: string,
  organizationsClinicsOptions: organizationClinicOption[],
  selectedOrganizationId: string,
  selectedShift: EdmMedicalAppShiftWithoutVisitFragment | undefined,
  selectedDay: Dayjs | undefined,
  startDate: Dayjs,
): ShiftFormSchemaType => {
  const defaultOrganization = selectedShift
    ? organizationsClinicsOptions.find(
        (org) => org.value === selectedShift.organizationId,
      )
    : organizationsClinicsOptions.find(
        (org) => org.value === selectedOrganizationId,
      );

  const defaultStaff = staffOptions?.find(
    (staff) => staff.value.id === selectedBlockStaffId,
  );

  const defaultVisitTypes = selectedShift
    ? selectedShift.visitTypes.map((visitType) => {
        return {
          label: visitTypeLabelMap[visitType],
          value: visitType,
        };
      })
    : [];

  const defaultWorkPlace = selectedShift
    ? workPlaceOptions.find((o) => o.value === selectedShift.workPlace)
    : workPlaceOptions[0];

  const defaultAcceptingNfz = selectedShift
    ? selectedShift.status === "ACCEPTING_NFZ" ||
      selectedShift.status === "ACCEPTING_BOTH"
      ? true
      : false
    : true;

  const defaultAcceptingPriv = selectedShift
    ? selectedShift.status === "ACCEPTING_PRIV" ||
      selectedShift.status === "ACCEPTING_BOTH"
      ? true
      : false
    : false;

  const defaultDay = selectedDay
    ? new Date(selectedDay.toDate())
    : startDate.get("month") === dayjs().get("month")
      ? new Date()
      : startDate.startOf("month").toDate();

  const isCustomVisitDuration = Boolean(
    selectedShift?.durationTimeMap &&
      Object.values(selectedShift.durationTimeMap).some(
        (value) => typeof value === "number",
      ),
  );

  return {
    organizationId: defaultOrganization!,
    staff: defaultStaff!,
    visitTypes: defaultVisitTypes,
    isCustomVisitDuration,
    overrideStaffVisitDuration: selectedShift?.durationTimeMap,
    workPlace: defaultWorkPlace!,
    acceptingNfz: defaultAcceptingNfz,
    acceptingPriv: defaultAcceptingPriv,
    changeRepeating: changeRepeatingOptions[0].value,
    availableDays: [],
    dateStartFrom: defaultDay,
    dateEnded: getLastBusinessDayOfMonth(defaultDay),
    timeFrom: selectedShift
      ? dayjs(selectedShift.startFrom).format("HH:mm")
      : "",
    timeTo: selectedShift ? dayjs(selectedShift.endedAt).format("HH:mm") : "",
    comment: selectedShift ? selectedShift.comment : undefined,
  };
};

export const getNonWorkingPeriodFormDefaultValues = (
  staffOptions: OptionWithSideElements<StaffOption>[] | undefined,
  selectedBlockStaffId: string,
  selectedNonWorkingPeriod: EdmNonWorkingPeriod | undefined,
  selectedDay: Dayjs | undefined,
  startDate: Dayjs,
) => {
  const defaultStaff = staffOptions?.find(
    (staff) => staff.value.id === selectedBlockStaffId,
  );
  const defaultLeaveReason = leaveTypeOptions.find(
    (o) => o.value === selectedNonWorkingPeriod?.leaveType,
  );
  const defaultDay = selectedDay
    ? new Date(selectedDay.toDate())
    : startDate.get("month") === dayjs().get("month")
      ? new Date()
      : startDate.startOf("month").toDate();

  return {
    staff: defaultStaff,
    leaveReason: defaultLeaveReason,
    day: defaultDay,
    timeFrom: selectedNonWorkingPeriod
      ? dayjs(selectedNonWorkingPeriod.startFrom).format("HH:mm")
      : undefined,
    timeTo: selectedNonWorkingPeriod
      ? dayjs(selectedNonWorkingPeriod.endedAt).format("HH:mm")
      : undefined,
    comment: selectedNonWorkingPeriod
      ? selectedNonWorkingPeriod.comment
      : undefined,
  };
};

export const formatTimeToDate = (timeString: string, selectedDate: Date) => {
  const [hours, minutes] = timeString.split(":");

  selectedDate.setHours(Number(hours));
  selectedDate.setMinutes(Number(minutes));

  return selectedDate;
};

export const sortShifts = (
  shifts: (EdmMedicalAppShiftWithoutVisitFragment | EdmNonWorkingPeriod)[],
) => {
  return shifts.sort((a, b) => {
    const aStart = dayjs(a.startFrom);
    const bStart = dayjs(b.startFrom);

    return aStart.valueOf() - bStart.valueOf();
  });
};

export const getNfzStatus = (acceptingNfz: boolean, acceptingPriv: boolean) => {
  if (acceptingNfz && acceptingPriv) {
    return "ACCEPTING_BOTH";
  }

  if (acceptingNfz) {
    return "ACCEPTING_NFZ";
  }

  return "ACCEPTING_PRIV";
};

export const getStaffRole = (genre: EdmGenreType, npwz: Maybe<string>) => {
  if (!npwz) {
    return "";
  }

  const isDoctor = genre === "DOCTOR";
  const isNurse = genre === "NURSE" && npwz.endsWith("P");

  if (isDoctor) {
    return "Lekarka rodzinna / Lekarz rodzinny";
  }

  if (isNurse) {
    return "Pielęgniarka / Pielęgniarz";
  }

  return "Położna / Położny";
};

export const isMedicalAppShift = (
  shift:
    | EdmMedicalAppShiftWithoutVisitFragment
    | EdmNonWorkingPeriod
    | undefined,
): shift is EdmMedicalAppShiftWithoutVisitFragment => {
  return shift?.__typename === "EdmMedicalAppShift";
};

export const isNonWorkingPeriod = (
  shift:
    | EdmMedicalAppShiftWithoutVisitFragment
    | EdmNonWorkingPeriod
    | undefined,
): shift is EdmNonWorkingPeriod => {
  return shift?.__typename === "EdmNonWorkingPeriod";
};

const getLastBusinessDayOfMonth = (date: Date): Date => {
  const lastDay = dayjs(date).endOf("month");
  const dayOfWeek = lastDay.day(); // 0 (ndz) - 6 (sob)

  const offset = dayOfWeek === 0 ? 2 : dayOfWeek === 6 ? 1 : 0;

  return lastDay.subtract(offset, "day").toDate();
};

export const filterMedicalStaffOptionsByGenre = (
  staff: Option<StaffOption>[],
  selectedStaffGenre: StaffGenreFilter,
) => {
  if (selectedStaffGenre === "ALL") {
    return staff;
  }

  return staff.filter((staff) => {
    if (selectedStaffGenre === "NURSE") return isNurse(staff.value);
    if (selectedStaffGenre === "MIDWIFE") return isMidwife(staff.value);
    return staff.value.genre === selectedStaffGenre;
  });
};
